import { Check } from "lucide-react";
import { cn } from "@util/tailwind";

interface CampaignWizardStepperProps {
    steps: string[];
    currentStep: number;
    onStepClick?: (step: number) => void;
    completedSteps?: number;
}

export function CampaignWizardStepper({
    steps,
    currentStep,
    onStepClick,
    completedSteps = 0,
}: CampaignWizardStepperProps) {
    return (
        <div className="flex items-center justify-center w-full px-4 py-2">
            {steps.map((label, index) => {
                const isCompleted = index < completedSteps;
                const isActive = index === currentStep;
                const isPending = index > completedSteps && !isActive;
                const isClickable = onStepClick && index <= completedSteps;

                return (
                    <div key={index} className="flex items-center">
                        {/* Step circle + label */}
                        <button
                            type="button"
                            onClick={() => isClickable && onStepClick(index)}
                            disabled={!isClickable}
                            className={cn(
                                "flex flex-col items-center gap-1.5 group",
                                isClickable ? "cursor-pointer" : "cursor-default"
                            )}
                        >
                            <div
                                className={cn(
                                    "w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold transition-all duration-200",
                                    isCompleted && "bg-blue-600 text-white",
                                    isActive && "border-2 border-blue-600 text-blue-600 bg-blue-50",
                                    isPending && "bg-gray-200 dark:bg-gray-700 text-gray-400 dark:text-gray-500",
                                    isClickable && "group-hover:ring-2 group-hover:ring-blue-200"
                                )}
                            >
                                {isCompleted ? (
                                    <Check className="w-4 h-4" />
                                ) : (
                                    index + 1
                                )}
                            </div>
                            <span
                                className={cn(
                                    "text-xs font-medium whitespace-nowrap",
                                    isActive && "text-blue-600",
                                    isCompleted && "text-gray-700 dark:text-gray-300",
                                    isPending && "text-gray-400 dark:text-gray-500"
                                )}
                            >
                                {label}
                            </span>
                        </button>

                        {/* Connecting line */}
                        {index < steps.length - 1 && (
                            <div
                                className={cn(
                                    "h-0.5 w-16 lg:w-24 mx-2 mt-[-18px] transition-colors duration-200",
                                    index < completedSteps
                                        ? "bg-blue-600"
                                        : "bg-gray-200 dark:bg-gray-700"
                                )}
                            />
                        )}
                    </div>
                );
            })}
        </div>
    );
}
